// src/state/predictions/helpers.ts

import { request, gql } from 'graphql-request'
import { GRAPH_API_PREDICTION } from 'config/constants/endpoints'
import { ethers } from 'ethers'
import {
  Bet,
  LedgerData,
  BetPosition,
  PredictionsState,
  PredictionStatus,
  ReduxNodeLedger,
  ReduxNodeRound,
  Round,
  RoundData,
  PredictionUser,
  HistoryFilter,
} from 'state/types'
import { multicallv2 } from 'utils/multicall'
import { getPredictionsContract } from 'utils/contractHelpers'
import predictionsAbi from 'config/abi/predictions.json'
import { getPredictionsAddress } from 'utils/addressHelpers'
import {
  BetResponse,
  getRoundBaseFields,
  getBetBaseFields,
  getUserBaseFields,
  RoundResponse,
  TotalWonMarketResponse,
  UserResponse,
} from './queries'
import { ROUNDS_PER_PAGE } from './config'

// --------------------------------------------------------------------------------
// Exported enum Result
// --------------------------------------------------------------------------------
export enum Result {
  WIN = 'win',
  LOSE = 'lose',
  CANCELED = 'canceled',
  LIVE = 'live',
  HOUSE = 'HOUSE',
}

// --------------------------------------------------------------------------------
// Types for multicall responses
// --------------------------------------------------------------------------------
export interface PredictionsRoundsResponse {
  epoch?: ethers.BigNumber
  startTimestamp?: ethers.BigNumber
  lockTimestamp?: ethers.BigNumber
  closeTimestamp?: ethers.BigNumber
  lockPrice?: ethers.BigNumber
  closePrice?: ethers.BigNumber
  totalAmount?: ethers.BigNumber
  bullAmount?: ethers.BigNumber
  bearAmount?: ethers.BigNumber
  rewardBaseCalAmount?: ethers.BigNumber
  rewardAmount?: ethers.BigNumber
  oracleCalled?: boolean
  lockOracleId?: ethers.BigNumber
  closeOracleId?: ethers.BigNumber
}

export interface PredictionsLedgerResponse {
  position: number
  amount: ethers.BigNumber
  claimed: boolean
}

// --------------------------------------------------------------------------------
// Utility to convert string to number or null
// --------------------------------------------------------------------------------
export const numberOrNull = (value: string) => {
  if (value === null) return null
  const valueNum = Number(value)
  return Number.isNaN(valueNum) ? null : valueNum
}

// --------------------------------------------------------------------------------
// Transform GraphQL BetResponse → Bet
// --------------------------------------------------------------------------------
export const transformBetResponse = (betResponse: BetResponse): Bet => {
  const bet: Bet = {
    id: betResponse.id,
    hash: betResponse.hash,
    amount: betResponse.amount ? parseFloat(betResponse.amount) : 0,
    position: betResponse.position === 'Bull' ? BetPosition.BULL : BetPosition.BEAR,
    claimed: betResponse.claimed,
    claimedAt: numberOrNull(betResponse.claimedAt),
    claimedBlock: numberOrNull(betResponse.claimedBlock),
    claimedHash: betResponse.claimedHash,
    createdAt: numberOrNull(betResponse.createdAt),
    updatedAt: numberOrNull(betResponse.updatedAt),
    claimedVSG: 0,
    claimedNetVSG: 0,
  }

  if (betResponse.user) {
    bet.user = transformUserResponse(betResponse.user)
  }

  // The subgraph no longer exposes Bet.round, so we skip it here
  return bet
}

// --------------------------------------------------------------------------------
// Transform GraphQL UserResponse → PredictionUser
//
// UserResponse now has only:
//   id, createdAt, updatedAt, totalBets, totalBetsBull, totalBetsBear
// --------------------------------------------------------------------------------
export const transformUserResponse = (
  userResponse: UserResponse
): PredictionUser => {
  const {
    id,
    createdAt,
    updatedAt,
    totalBets,
    totalBetsBull,
    totalBetsBear,
  } = userResponse

  return {
    id,
    createdAt: numberOrNull(createdAt),
    updatedAt: numberOrNull(updatedAt),
    totalBets: numberOrNull(totalBets),
    totalBetsBull: numberOrNull(totalBetsBull),
    totalBetsBear: numberOrNull(totalBetsBear),
  } as PredictionUser
}

// --------------------------------------------------------------------------------
// Transform GraphQL RoundResponse → Round
//
// RoundResponse fields:
//   id, epoch, startTimestamp, lockTimestamp, closeTimestamp,
//   lockPrice, closePrice, totalAmount, bullAmount, bearAmount, status
// --------------------------------------------------------------------------------
export const transformRoundResponse = (roundResponse: RoundResponse): Round => {
  const {
    id,
    epoch,
    startTimestamp,
    lockTimestamp,
    closeTimestamp,
    lockPrice,
    closePrice,
    totalAmount,
    bullAmount,
    bearAmount,
    status,
  } = roundResponse

  const epochNum = numberOrNull(epoch)
  const startAtNum = numberOrNull(startTimestamp)
  const lockAtNum = numberOrNull(lockTimestamp)
  const closeAtNum = numberOrNull(closeTimestamp)
  const lockPriceNum = lockPrice ? parseFloat(lockPrice) : 0
  const closePriceNum = closePrice ? parseFloat(closePrice) : 0
  const totalAmountNum = totalAmount ? parseFloat(totalAmount) : 0
  const bullAmountNum = bullAmount ? parseFloat(bullAmount) : 0
  const bearAmountNum = bearAmount ? parseFloat(bearAmount) : 0

  // Interpret status; assume "2" = failed
  const failed = status === '2'

  return {
    id,
    epoch: epochNum,
    failed,
    startAt: startAtNum,
    startBlock: null,
    startHash: null,
    lockAt: lockAtNum,
    lockBlock: null,
    lockHash: null,
    lockPrice: lockPriceNum,
    lockRoundId: null,
    closeAt: closeAtNum,
    closeBlock: null,
    closeHash: null,
    closePrice: closePriceNum,
    closeRoundId: null,
    totalBets: null,
    totalAmount: totalAmountNum,
    bullBets: null,
    bullAmount: bullAmountNum,
    bearBets: null,
    bearAmount: bearAmountNum,
    bets: [],
    position: null, // Added to satisfy Round type
  }
}

// --------------------------------------------------------------------------------
// Determine outcome of a bet given currentEpoch
// --------------------------------------------------------------------------------
export const getRoundResult = (bet: Bet, currentEpoch: number): Result => {
  const { round } = bet
  if (round.failed) {
    return Result.CANCELED
  }
  if (round.epoch !== null && round.epoch >= currentEpoch - 1) {
    return Result.LIVE
  }
  const roundResultPosition =
    round.closePrice > round.lockPrice ? BetPosition.BULL : BetPosition.BEAR
  return bet.position === roundResultPosition ? Result.WIN : Result.LOSE
}

// --------------------------------------------------------------------------------
// Filter bets by HistoryFilter
// --------------------------------------------------------------------------------
export const getFilteredBets = (bets: Bet[], filter: HistoryFilter) => {
  switch (filter) {
    case HistoryFilter.COLLECTED:
      return bets.filter((bet) => bet.claimed === true)
    case HistoryFilter.UNCOLLECTED:
      return bets.filter((bet) => {
        return (
          !bet.claimed &&
          (bet.position === bet.round.position || bet.round.failed === true)
        )
      })
    case HistoryFilter.ALL:
    default:
      return bets
  }
}

// --------------------------------------------------------------------------------
// Fetch total won from the subgraph
// --------------------------------------------------------------------------------
export const getTotalWon = async (): Promise<number> => {
  const { market } = (await request(
    GRAPH_API_PREDICTION,
    gql`
      query getTotalWonData {
        market(id: 1) {
          totalBESC
          totalBESCTreasury
        }
      }
    `
  )) as { market: TotalWonMarketResponse }

  const totalBESC = market.totalBESC ? parseFloat(market.totalBESC) : 0
  const totalBESCTreasury = market.totalBESCTreasury
    ? parseFloat(market.totalBESCTreasury)
    : 0

  return Math.max(totalBESC - totalBESCTreasury, 0)
}

type WhereClause = Record<string, string | number | boolean | string[]>

// --------------------------------------------------------------------------------
// GraphQL queries to fetch bet history
// --------------------------------------------------------------------------------
export const getBetHistory = async (
  where: WhereClause = {},
  first = 1000,
  skip = 0
): Promise<BetResponse[]> => {
  const response = await request(
    GRAPH_API_PREDICTION,
    gql`
      query getBetHistory($first: Int!, $skip: Int!, $where: Bet_filter) {
        bets(first: $first, skip: $skip, where: $where, orderBy: createdAt, orderDirection: desc) {
          id
          hash
          amount
          position
          claimed
          claimedAt
          claimedHash
          createdAt
          updatedAt
          user {
            id
            createdAt
            updatedAt
            totalBets
            totalBetsBull
            totalBetsBear
          }
        }
      }
    `,
    { first, skip, where }
  )
  return response.bets
}

export const getBet = async (betId: string): Promise<BetResponse> => {
  const response = await request(
    GRAPH_API_PREDICTION,
    gql`
      query getBet($id: ID!) {
        bet(id: $id) {
          id
          hash
          amount
          position
          claimed
          claimedAt
          claimedHash
          createdAt
          updatedAt
          user {
            id
            createdAt
            updatedAt
            totalBets
            totalBetsBull
            totalBetsBear
          }
        }
      }
    `,
    {
      id: betId.toLowerCase(),
    }
  )
  return response.bet
}

// --------------------------------------------------------------------------------
// Multicall to on‐chain methods (ledger)
// --------------------------------------------------------------------------------
export const getLedgerData = async (
  account: string,
  epochs: number[]
) => {
  const address = getPredictionsAddress()
  const ledgerCalls = epochs.map((epoch) => ({
    address,
    name: 'ledger',
    params: [epoch, account],
  }))
  const response = await multicallv2<PredictionsLedgerResponse[]>(predictionsAbi, ledgerCalls)
  return response
}

// --------------------------------------------------------------------------------
// Pagination constant
// --------------------------------------------------------------------------------
export const LEADERBOARD_RESULTS_PER_PAGE = 20

interface GetPredictionUsersOptions {
  skip?: number
  first?: number
  orderBy?: string
  orderDir?: string
  where?: WhereClause
}

const defaultPredictionUserOptions = {
  skip: 0,
  first: LEADERBOARD_RESULTS_PER_PAGE,
  orderBy: 'createdAt',
  orderDir: 'desc',
}

// --------------------------------------------------------------------------------
// Query users for leaderboard
// --------------------------------------------------------------------------------
export const getPredictionUsers = async (
  options: GetPredictionUsersOptions = {}
): Promise<UserResponse[]> => {
  const { first, skip, where, orderBy, orderDir } = {
    ...defaultPredictionUserOptions,
    ...options,
  }
  const response = await request(
    GRAPH_API_PREDICTION,
    gql`
      query getUsers(
        $first: Int!
        $skip: Int!
        $where: User_filter
        $orderBy: User_orderBy
        $orderDir: OrderDirection
      ) {
        users(
          first: $first
          skip: $skip
          where: $where
          orderBy: $orderBy
          orderDirection: $orderDir
        ) {
          id
          createdAt
          updatedAt
          totalBets
          totalBetsBull
          totalBetsBear
        }
      }
    `,
    { first, skip, where, orderBy, orderDir }
  )
  return response.users
}

export const getPredictionUser = async (
  account: string
): Promise<UserResponse> => {
  const response = await request(
    GRAPH_API_PREDICTION,
    gql`
      query getUser($id: ID!) {
        user(id: $id) {
          id
          createdAt
          updatedAt
          totalBets
          totalBetsBull
          totalBetsBear
        }
      }
    `,
    {
      id: account.toLowerCase(),
    }
  )
  return response.user
}

// --------------------------------------------------------------------------------
// Fetch market data from contract with corrected destructuring
// --------------------------------------------------------------------------------
export type MarketData = Pick<
  PredictionsState,
  'status' | 'currentEpoch' | 'intervalSeconds' | 'minBetAmount' | 'bufferSeconds'
>
export const getPredictionData = async (): Promise<MarketData> => {
  const address = getPredictionsAddress()
  const staticCalls = [
    'currentEpoch',
    'intervalSeconds',
    'minBetAmount',
    'paused',
    'bufferSeconds',
  ].map((method) => ({
    address,
    name: method,
  }))

  // Each multicall returns an array of single-element tuples.
  const results: Array<[ethers.BigNumber] | [boolean]> = await multicallv2<any>(
    predictionsAbi,
    staticCalls
  )

  const currentEpoch = (results[0] as [ethers.BigNumber])[0]
  const intervalSeconds = (results[1] as [ethers.BigNumber])[0]
  const minBetAmount = (results[2] as [ethers.BigNumber])[0]
  const paused = (results[3] as [boolean])[0]
  const bufferSeconds = (results[4] as [ethers.BigNumber])[0]

  return {
    status: paused ? PredictionStatus.PAUSED : PredictionStatus.LIVE,
    currentEpoch: currentEpoch.toNumber(),
    intervalSeconds: intervalSeconds.toNumber(),
    minBetAmount: minBetAmount.toString(),
    bufferSeconds: bufferSeconds.toNumber(),
  }
}

// --------------------------------------------------------------------------------
// Fetch on‐chain rounds data via multicall
// --------------------------------------------------------------------------------
export const getRoundsData = async (
  epochs: number[]
): Promise<PredictionsRoundsResponse[]> => {
  const address = getPredictionsAddress()
  const calls = epochs.map((epoch) => ({
    address,
    name: 'rounds',
    params: [epoch],
  }))
  const response = await multicallv2<PredictionsRoundsResponse[]>(predictionsAbi, calls)
  return response
}

export const makeFutureRoundResponse = (
  epoch: number,
  startTimestamp: number
): ReduxNodeRound => {
  return {
    epoch,
    startTimestamp,
    lockTimestamp: 0,
    closeTimestamp: 0,
    lockPrice: ethers.BigNumber.from(0).toJSON(),
    closePrice: ethers.BigNumber.from(0).toJSON(),
    totalAmount: ethers.BigNumber.from(0).toJSON(),
    bullAmount: ethers.BigNumber.from(0).toJSON(),
    bearAmount: ethers.BigNumber.from(0).toJSON(),
    rewardBaseCalAmount: ethers.BigNumber.from(0).toJSON(),
    rewardAmount: ethers.BigNumber.from(0).toJSON(),
    oracleCalled: false,
    lockOracleId: '0',
    closeOracleId: '0',
  }
}

export const makeRoundData = (rounds: ReduxNodeRound[]): RoundData => {
  return rounds.reduce((accum, round) => {
    return {
      ...accum,
      [round.epoch.toString()]: round,
    }
  }, {} as RoundData)
}

// --------------------------------------------------------------------------------
// Convert multicall round response → ReduxNodeRound
// --------------------------------------------------------------------------------
export const serializePredictionsRoundsResponse = (
  response: PredictionsRoundsResponse
): ReduxNodeRound => {
  // Always provide a BigNumber instance, never null
  const epochBN = response?.epoch ?? ethers.BigNumber.from(0)
  const startTSBN = response?.startTimestamp ?? ethers.BigNumber.from(0)
  const lockTSBN = response?.lockTimestamp ?? ethers.BigNumber.from(0)
  const closeTSBN = response?.closeTimestamp ?? ethers.BigNumber.from(0)
  const lockPriceBN = response?.lockPrice ?? ethers.BigNumber.from(0)
  const closePriceBN = response?.closePrice ?? ethers.BigNumber.from(0)
  const totalAmountBN = response?.totalAmount ?? ethers.BigNumber.from(0)
  const bullAmountBN = response?.bullAmount ?? ethers.BigNumber.from(0)
  const bearAmountBN = response?.bearAmount ?? ethers.BigNumber.from(0)
  const rewardBaseCalBN = response?.rewardBaseCalAmount ?? ethers.BigNumber.from(0)
  const rewardAmountBN = response?.rewardAmount ?? ethers.BigNumber.from(0)
  const oracleCalledVal = response?.oracleCalled ?? false
  const lockOracleIdBN = response?.lockOracleId ?? ethers.BigNumber.from(0)
  const closeOracleIdBN = response?.closeOracleId ?? ethers.BigNumber.from(0)

  return {
    oracleCalled: oracleCalledVal,
    epoch: epochBN.toNumber(),
    startTimestamp: startTSBN.toNumber(),
    lockTimestamp: lockTSBN.toNumber(),
    closeTimestamp: closeTSBN.toNumber(),
    lockPrice: lockPriceBN.toJSON(),
    closePrice: closePriceBN.toJSON(),
    totalAmount: totalAmountBN.toJSON(),
    bullAmount: bullAmountBN.toJSON(),
    bearAmount: bearAmountBN.toJSON(),
    rewardBaseCalAmount: rewardBaseCalBN.toJSON(),
    rewardAmount: rewardAmountBN.toJSON(),
    lockOracleId: lockOracleIdBN.toString(),
    closeOracleId: closeOracleIdBN.toString(),
  }
}

// --------------------------------------------------------------------------------
// Convert multicall ledger response → ReduxNodeLedger
// --------------------------------------------------------------------------------
export const serializePredictionsLedgerResponse = (
  ledgerResponse: PredictionsLedgerResponse
): ReduxNodeLedger => {
  const { position, amount, claimed } = ledgerResponse
  return {
    position: position === 0 ? BetPosition.BULL : BetPosition.BEAR,
    amount: amount.toJSON(),
    claimed,
  }
}

export const makeLedgerData = (
  account: string,
  ledgers: PredictionsLedgerResponse[],
  epochs: number[]
): LedgerData => {
  return ledgers.reduce((accum, ledgerResponse, index) => {
    if (!ledgerResponse) {
      return accum
    }
    const { amount } = ledgerResponse
    if (amount.eq(0)) {
      return accum
    }
    const epochKey = epochs[index].toString()
    return {
      ...accum,
      [account]: {
        ...accum[account],
        [epochKey]: serializePredictionsLedgerResponse(ledgerResponse),
      },
    }
  }, {} as LedgerData)
}

export const fetchUsersRoundsLength = async (account: string) => {
  try {
    const contract = getPredictionsContract()
    const length = await contract.getUserRoundsLength(account)
    return length
  } catch {
    return ethers.BigNumber.from(0)
  }
}

/**
 * Fetches rounds a user has participated in
 */
export const fetchUserRounds = async (
  account: string,
  cursor = 0,
  size = ROUNDS_PER_PAGE
): Promise<{ [key: string]: ReduxNodeLedger }> => {
  const contract = getPredictionsContract()

  try {
    const [rounds, ledgers] = await contract.getUserRounds(
      account,
      cursor,
      size
    )

    return rounds.reduce((accum, round, index) => {
      return {
        ...accum,
        [round.toString()]: serializePredictionsLedgerResponse(
          ledgers[index]
        ),
      }
    }, {} as { [key: string]: ReduxNodeLedger })
  } catch {
    // When the results run out, return null
    return null
  }
}

/**
 * Convert any nested BigNumber-JSON objects back into actual BigNumber instances
 */
export const parseBigNumberObj = <
  T extends Record<string, any>,
  K extends Record<string, any>
>(
  data: T
): K => {
  return Object.keys(data).reduce((accum, key) => {
    const value = data[key]
    if (value && (value as any).type === 'BigNumber') {
      return {
        ...accum,
        [key]: ethers.BigNumber.from(value),
      }
    }
    return {
      ...accum,
      [key]: value,
    }
  }, {}) as K
}
